// Editorial "Overview" prose generator for a college page.
//
// Composes 4–6 varied, natural-language sentences purely from data we already
// have (type, district, NAAC grade, fees, hostel, branches, status). It uses
// multiple sentence templates per attribute and selects between them
// deterministically from a hash of the college code, so no two colleges read
// identically — this is what turns a templated data dump into unique prose and
// addresses the AdSense "scaled content" concern.
//
// IMPORTANT: This module is imported by BOTH the client bundle
// (CollegeDetails.tsx) and the Node build-time prerender script
// (scripts/prerender.ts). It must therefore stay free of any browser-only or
// Vite-only APIs (no `window`, no `import.meta.env`, no CSS imports). No facts
// are invented — every sentence is derived from the passed fields.

export interface OverviewCollege {
  college_code: string;
  college_name: string;
  college_type: string;
  district?: { name?: string | null } | null;
  naac_grade?: string | null;
  autonomous_status?: boolean;
  womens_college?: boolean;
  minority_institution?: boolean;
  annual_tuition_fee?: number | string | null;
  boys_accommodation_available?: boolean;
  girls_accommodation_available?: boolean;
  transport_facilities?: boolean;
  nearest_railway_station?: string | null;
  distance_from_district_hq?: number | string | null;
}

export interface OverviewCourseLike {
  branch_name?: string | null;
  branch_code?: string | null;
}

export interface OverviewOpts {
  // If provided, fee positioning is described relative to this state average.
  // Otherwise fixed thresholds are used.
  stateAvgFee?: number;
}

// ── deterministic helpers (stable across build + client) ────────────────────

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}

function toNum(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

// The `college_name` field sometimes has address details appended after a
// comma (a data-entry convention from the source records), which reads
// awkwardly in prose. Mirrors the truncation convention already used for
// display in CollegeDetails.tsx's `shortName()`.
export function shortCollegeName(name: string): string {
  return name.length > 80 && name.includes(',') ? name.substring(0, name.indexOf(',')) : name;
}

// Human-readable college type
function displayType(raw: string): string {
  const t = (raw || '').toLowerCase();
  if (t.includes('anna')) return 'Anna University';
  if (t.includes('private') || t.includes('self')) return 'self-financing';
  if (t.includes('aided')) return 'government-aided';
  if (t.includes('government') || t.includes('govt')) return 'government';
  return raw || 'engineering';
}

// NAAC tier bucket → 0 top, 1 strong, 2 mid, 3 none
function naacTier(grade: string | null | undefined): number {
  if (!grade) return 3;
  const g = grade.toUpperCase().replace(/\s/g, '');
  if (g === 'A++' || g === 'A+') return 0;
  if (g === 'A') return 1;
  if (g.startsWith('B') || g.startsWith('C')) return 2;
  return 3;
}

// Detect notable branches present (for "strengths" without inventing data)
const BRANCH_KEYWORDS: Array<{ label: string; re: RegExp }> = [
  { label: 'Computer Science', re: /computer|cse\b/i },
  { label: 'Information Technology', re: /information tech|\bit\b/i },
  { label: 'Artificial Intelligence & Data Science', re: /artificial intelligence|data science|\bai\b|machine learning/i },
  { label: 'Electronics & Communication', re: /electronics and communication|\bece\b|electronics & comm/i },
  { label: 'Electrical & Electronics', re: /electrical|\beee\b/i },
  { label: 'Mechanical', re: /mechanical|\bmech\b/i },
  { label: 'Civil', re: /civil/i },
];

function notableBranches(courses: OverviewCourseLike[]): string[] {
  const names = courses.map(c => `${c.branch_name || ''} ${c.branch_code || ''}`);
  const found: string[] = [];
  for (const { label, re } of BRANCH_KEYWORDS) {
    if (names.some(n => re.test(n))) found.push(label);
  }
  return found;
}

// ── main generator ──────────────────────────────────────────────────────────

/**
 * Returns an array of complete sentences (4–6) forming the Overview paragraph.
 * Consumers join them with a space and render as plain text / <p>.
 */
export function buildCollegeOverview(
  college: OverviewCollege,
  courses: OverviewCourseLike[] = [],
  opts: OverviewOpts = {},
): string[] {
  const seed = hashCode(college.college_code || college.college_name || '');
  const name = shortCollegeName(college.college_name);
  const district = college.district?.name || 'Tamil Nadu';
  const type = displayType(college.college_type);
  const sentences: string[] = [];

  // 1) Intro — type + location. Anna University constituent campuses get
  // their own phrasing to avoid "Anna University-affiliated Anna University
  // college" redundancy.
  const introTemplates = type === 'Anna University'
    ? [
        `${name} is a constituent campus of Anna University, located in ${district}, Tamil Nadu, and admits students through the TNEA single-window counselling process.`,
        `Situated in ${district}, ${name} is one of Anna University's own constituent colleges, participating in Tamil Nadu Engineering Admissions (TNEA) counselling every year.`,
      ]
    : [
        `${name} is a ${type} engineering college located in ${district}, Tamil Nadu, admitting students through the TNEA single-window counselling process.`,
        `Situated in ${district}, ${name} is a ${type} engineering institution that participates in Tamil Nadu Engineering Admissions (TNEA) counselling every year.`,
        `${name} is an Anna University–affiliated ${type} engineering college in ${district} district, and takes admissions via the state-run TNEA counselling.`,
      ];
  sentences.push(pick(introTemplates, seed));

  // 2) NAAC grade meaning
  const tier = naacTier(college.naac_grade);
  const grade = college.naac_grade;
  if (tier === 0) {
    sentences.push(pick([
      `Its NAAC grade of ${grade} places it among the top-accredited engineering colleges in the state, reflecting strong performance on academic quality and infrastructure.`,
      `The college holds a NAAC ${grade} accreditation — one of the highest bands — which signals well-regarded teaching, research and campus facilities.`,
    ], seed));
  } else if (tier === 1) {
    sentences.push(pick([
      `With a NAAC grade of ${grade}, it is a strongly accredited institution that meets nationally recognised standards for academic quality.`,
      `The college's NAAC ${grade} accreditation indicates a solid, well-established academic setup.`,
    ], seed));
  } else if (tier === 2) {
    sentences.push(pick([
      `It carries a NAAC grade of ${grade}, indicating an accredited campus that has cleared the national quality-assessment process.`,
      `The institution is NAAC-accredited with a grade of ${grade}.`,
    ], seed));
  } else {
    sentences.push(pick([
      `A NAAC accreditation grade is not currently listed for the college; prospective students may wish to confirm its latest accreditation status directly.`,
      `The college does not have a NAAC grade on record here, so it is worth checking its current accreditation before finalising choices.`,
    ], seed));
  }

  // 3) Fee positioning
  const fee = toNum(college.annual_tuition_fee);
  if (fee != null && fee > 0) {
    const feeStr = `₹${Math.round(fee).toLocaleString('en-IN')}`;
    let position: 'low' | 'mid' | 'high';
    if (opts.stateAvgFee && opts.stateAvgFee > 0) {
      if (fee < opts.stateAvgFee * 0.75) position = 'low';
      else if (fee > opts.stateAvgFee * 1.25) position = 'high';
      else position = 'mid';
    } else {
      position = fee < 15000 ? 'low' : fee < 60000 ? 'mid' : 'high';
    }
    const feeLines: Record<string, string[]> = {
      low: [
        `Its published government-quota tuition of about ${feeStr} per year is on the lower, more affordable end for Tamil Nadu engineering colleges.`,
        `At roughly ${feeStr} per year in the government quota, tuition here is budget-friendly compared with most self-financing colleges in the state.`,
      ],
      mid: [
        `The government-quota tuition works out to around ${feeStr} per year, placing it in the mid-range of Tamil Nadu engineering fees.`,
        `Annual tuition of about ${feeStr} keeps the college in the moderate fee band for the state.`,
      ],
      high: [
        `With annual tuition of around ${feeStr}, the college sits at the higher end of the fee spectrum, which families should factor into their budget.`,
        `Tuition of roughly ${feeStr} per year is on the premium side for Tamil Nadu engineering colleges.`,
      ],
    };
    sentences.push(pick(feeLines[position], seed));
  }

  // 4) Branch strengths (only from branches actually offered)
  const branches = notableBranches(courses);
  if (branches.length >= 2) {
    const list = branches.slice(0, 3).join(', ');
    sentences.push(pick([
      `Popular branches on offer include ${list}, alongside other undergraduate engineering programmes.`,
      `Students here can choose from sought-after branches such as ${list} among the college's engineering courses.`,
    ], seed));
  } else if (courses.length > 0) {
    sentences.push(`The college offers ${courses.length} undergraduate engineering ${courses.length === 1 ? 'branch' : 'branches'} across its departments.`);
  }

  // 5) Hostel availability
  const b = college.boys_accommodation_available;
  const g = college.girls_accommodation_available;
  if (b && g) {
    sentences.push(pick([
      `On-campus hostel accommodation is available for both boys and girls, which helps students coming from other districts.`,
      `Both boys' and girls' hostels are available, making it a practical option for outstation students.`,
    ], seed));
  } else if (b) {
    sentences.push(`Hostel accommodation is available for boys; girls' students may need to arrange nearby accommodation.`);
  } else if (g) {
    sentences.push(`Hostel accommodation is available for girls; boys' students may need to arrange nearby accommodation.`);
  } else {
    sentences.push(`The college does not list on-campus hostel facilities, so day-scholar or private accommodation would apply.`);
  }

  // 6) One distinguishing attribute (autonomous / women's / minority / access)
  const extras: string[] = [];
  if (college.autonomous_status) extras.push(`It enjoys autonomous status, giving it flexibility in designing its own curriculum and examinations.`);
  if (college.womens_college) extras.push(`It is a women's college, admitting female candidates only.`);
  if (college.minority_institution) extras.push(`It is run as a minority institution.`);
  if (college.transport_facilities) extras.push(`College transport is provided, easing the daily commute for local students.`);
  const railway = college.nearest_railway_station;
  if (railway) extras.push(`The nearest railway station is ${railway}, adding to the campus's connectivity.`);
  if (extras.length > 0) sentences.push(pick(extras, seed));

  return sentences;
}
